import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { HONEYPOT_TYPES } from '../types/honeypot';
import { Header } from '../components/Header';
import './HoneypotConfigPage.css';

export const HoneypotConfigPage: React.FC = () => {
  const { siteConfig, updateSiteConfig, user, logout } = useAuth();
  const navigate = useNavigate();

  // Site configuration
  const [siteUrl, setSiteUrl] = useState(siteConfig?.siteUrl || '');
  const [backendUrl, setBackendUrl] = useState(siteConfig?.backendUrl || '');
  const [siteDescription, setSiteDescription] = useState(siteConfig?.siteDescription || '');

  // Selected honeypots
  const [selectedHoneypots, setSelectedHoneypots] = useState<string[]>(
    siteConfig?.selectedHoneypots || ['web']
  );

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleHoneypot = (id: string) => {
    const honeypot = HONEYPOT_TYPES.find(h => h.id === id);
    if (honeypot?.status !== 'implemented') {
      return;
    }
    setSelectedHoneypots(prev =>
      prev.includes(id) ? prev.filter(h => h !== id) : [...prev, id]
    );
  };

  const calculateTotal = () => {
    return selectedHoneypots.reduce((total, id) => {
      const honeypot = HONEYPOT_TYPES.find(h => h.id === id);
      return total + (honeypot?.price || 0);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (selectedHoneypots.length === 0) {
      setError('Please select at least one honeypot type');
      return;
    }

    setLoading(true);
    try {
      await updateSiteConfig(siteUrl, backendUrl, siteDescription, selectedHoneypots);
      setSuccess('Configuration saved successfully!');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '#00ff87';
      case 'medium': return '#ffa500';
      case 'hard': return '#ff4757';
      default: return '#fff';
    }
  };

  const getValueColor = (value: string) => {
    switch (value) {
      case 'very-high': return '#00ff87';
      case 'high': return '#48dbfb';
      case 'medium': return '#ffa500';
      case 'low': return '#95afc0';
      default: return '#fff';
    }
  };

  return (
    <div className="honeypot-config-page">
      {/* Animated Background */}
      <div className="honeypot-background">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-gradient-3"></div>
      </div>

      {/* Header */}
      <Header
        title="Honeypot Configuration"
        subtitle="Configure your site and deploy security honeypots"
        showBackButton={true}
      />

      {/* Container */}
      <div className="honeypot-container">
        {/* Site Configuration Section */}
        <div className="config-card">
          <div className="card-header">
            <h2>🌐 Site Configuration</h2>
            <p>Enter the details of the site you want to protect</p>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>Site URL *</label>
              <input
                type="url"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                required
                placeholder="https://example.com"
              />
            </div>
            <div className="form-group">
              <label>Backend URL *</label>
              <input
                type="url"
                value={backendUrl}
                onChange={(e) => setBackendUrl(e.target.value)}
                required
                placeholder="https://api.example.com"
              />
            </div>
            <div className="form-group">
              <label>Site Description *</label>
              <textarea
                value={siteDescription}
                onChange={(e) => setSiteDescription(e.target.value)}
                required
                placeholder="Describe what this site is about..."
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Honeypot Selection Section */}
        <div className="config-card honeypot-selection">
          <div className="card-header">
            <h2>🎯 Select Your Honeypots</h2>
            <p>Choose the types of honeypots you want to deploy to protect your site</p>
          </div>
          <div className="card-body">
            <div className="honeypot-grid">
              {HONEYPOT_TYPES.map((honeypot) => {
                const isSelected = selectedHoneypots.includes(honeypot.id);
                const isDisabled = honeypot.status !== 'implemented';
                return (
                  <div
                    key={honeypot.id}
                    className={`honeypot-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && toggleHoneypot(honeypot.id)}
                  >
                    {isDisabled && <div className="coming-soon-badge">Coming Soon</div>}
                    {isSelected && <div className="selected-badge">✓</div>}
                    <div className="honeypot-icon">{honeypot.icon}</div>
                    <h3 className="honeypot-name">{honeypot.name}</h3>
                    <div className="honeypot-meta">
                      <span
                        className="difficulty-badge"
                        style={{ borderColor: getDifficultyColor(honeypot.difficulty) }}
                      >
                        {honeypot.difficulty}
                      </span>
                      <span
                        className="value-badge"
                        style={{ borderColor: getValueColor(honeypot.value) }}
                      >
                        {honeypot.value} value
                      </span>
                    </div>
                    <p className="honeypot-description">{honeypot.description}</p>
                    <ul className="honeypot-features">
                      {honeypot.features.map((feature, idx) => (
                        <li key={idx}>• {feature}</li>
                      ))}
                    </ul>
                    <div className="honeypot-price">
                      {honeypot.price === 0 ? (
                        <span className="price-free">FREE</span>
                      ) : (
                        <>
                          <span className="price-amount">${honeypot.price}</span>
                          <span className="price-period">/month</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Summary & Submit */}
        <div className="config-card summary-card">
          <div className="card-header">
            <h2>📊 Configuration Summary</h2>
            <p>Review and deploy your honeypot configuration</p>
          </div>
          <div className="card-body">
            <div className="summary-content">
              <div className="summary-left">
                <div className="summary-details">
                  <div className="summary-item">
                    <span className="summary-label">Selected Honeypots:</span>
                    <span className="summary-value">{selectedHoneypots.length}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Total Monthly Cost:</span>
                    <span className="summary-value total-price">
                      {calculateTotal() === 0 ? 'FREE' : `$${calculateTotal()}/month`}
                    </span>
                  </div>
                </div>
                {selectedHoneypots.length > 0 && (
                  <div className="selected-list">
                    {selectedHoneypots.map(id => {
                      const honeypot = HONEYPOT_TYPES.find(h => h.id === id);
                      return (
                        <span key={id} className="selected-tag">
                          {honeypot?.icon} {honeypot?.name}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="summary-right">
                {error && <div className="error-message">{error}</div>}
                {success && <div className="success-message">{success}</div>}
                <button
                  onClick={handleSubmit}
                  className="deploy-button"
                  disabled={loading || selectedHoneypots.length === 0 || !siteUrl || !backendUrl || !siteDescription}
                >
                  {loading ? '⏳ Deploying...' : '🚀 Deploy Configuration'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};